// SQL text for identifiers the suggestion / VQE emitters write into a buffer.
//
// Two different sources of a name need two different rules, and mixing them is
// how display labels ended up emitted as SQL (`order items."qty"`,
// `vqemimixed."status"`):
//
//   - a CATALOG spelling (schema snapshot, FK edge, MetaInfo declaration) is
//     the server's own bytes. It is written bare only when this engine reads
//     the bare spelling back to exactly those bytes, else delimited in the
//     engine's own style (`"order items"`, `[Order Details]`, `` `order items` ``,
//     Oracle `"VqeMixed"`).
//   - a name the QUERY bound (a FROM item's table token, an alias) is written
//     the way the query spelled it: a delimited leaf keeps its delimiters (and
//     its case — `"VqeMiMixed"` stays `"VqeMiMixed"`), a bare one stays bare
//     (the server folds it the same way again).
//
// Pure; the quoting rules themselves live in `ddl/apply/dialect.quoting.ts`.

import type { QualifiedName, SqlEngine } from '../../sql-grammar/model';
import {
  ENGINE_DEFAULT_QUOTING,
  inferQuotingFromRaw,
  needsDelimitersToNameExactly,
  needsQuoting,
  quoteWithStyle,
} from '../ddl/apply/dialect.quoting';
import { foldIdentifier } from '../scope/identifiers';

/** A catalog-held name as SQL: bare when that is exact, else delimited. */
export function catalogIdentSql(name: string, engine: SqlEngine): string {
  return needsDelimitersToNameExactly(name, engine)
    ? quoteWithStyle(name, ENGINE_DEFAULT_QUOTING[engine])
    : name;
}

/**
 * The catalog spelling of a name a MetaInfo declaration (a loose FK's column,
 * a filter's field) names — to be written through {@link catalogIdentSql}.
 * Among the owning relation's `catalogNames`: the exact spelling, else the
 * only one equal to it case-insensitively (an import spelling Oracle's
 * `CUSTOMER_ID` as `customer_id`). A name that resolves to none is taken AS
 * the catalog spelling: declarations made in the app record the server's own
 * spelling, and the column list is routinely unknown — empty while a
 * PostgreSQL / Oracle snapshot's columns still load — so folding it there
 * rewrote a valid `"CustomerId"` to `customerid` (PostgreSQL) and a quoted
 * `"customer_id"` to `CUSTOMER_ID` (Oracle). The same answer whether the
 * list is unknown, lacks the column, or holds two case variants of it, so
 * every surface that writes the name (IntelliSense and VQE JOINs, their
 * filter fields) agrees in every loading state.
 */
export function declaredCatalogSpelling(declared: string, catalogNames: readonly string[] | undefined): string {
  const exact = catalogNames?.find((n) => n === declared);
  if (exact !== undefined) return exact;
  const lower = declared.toLowerCase();
  const folded = catalogNames?.filter((n) => n.toLowerCase() === lower) ?? [];
  return folded.length === 1 ? folded[0]! : declared;
}

/** `schema.name` of a catalog relation, each part through {@link catalogIdentSql}. */
export function catalogRelationSql(qualified: QualifiedName, engine: SqlEngine): string {
  const name = catalogIdentSql(qualified.name, engine);
  return qualified.schema ? `${catalogIdentSql(qualified.schema, engine)}.${name}` : name;
}

/**
 * The leaf (table) token of a query-bound relation, spelled as the query wrote
 * it. A leaf with no bare form is delimited even when the binding carries no
 * quoting info (hand-built names), since writing it bare cannot be valid.
 */
export function boundLeafSql(qualified: QualifiedName, engine: SqlEngine): string {
  const style = qualified.quoting ?? leafQuotingFromRaw(qualified, engine);
  if (style !== 'unquoted') return quoteWithStyle(qualified.name, style);
  if (needsQuoting(qualified.name, engine)) {
    return quoteWithStyle(qualified.name, ENGINE_DEFAULT_QUOTING[engine]);
  }
  return qualified.name;
}

/**
 * An alias as a column qualifier. `alias` is the parsed (undelimited) text and
 * `aliasFolded` the resolver's fold of it — for a delimited alias the fold is
 * the text itself. It is delimited when it has no bare form, or when it was
 * typed delimited with a spelling the engine's fold would change (PostgreSQL
 * `"Ord"`, Oracle `"o"`): written bare, those name a different alias.
 */
export function aliasSql(alias: string, aliasFolded: string | undefined, engine: SqlEngine): string {
  const typedDelimited =
    aliasFolded !== undefined &&
    aliasFolded === alias &&
    foldIdentifier(alias, false, { engine }) !== alias &&
    foldsCase(engine);
  if (needsQuoting(alias, engine) || typedDelimited) {
    return quoteWithStyle(alias, ENGINE_DEFAULT_QUOTING[engine]);
  }
  return alias;
}

/** Engines whose bare-identifier fold changes case (so case is identity when delimited). */
function foldsCase(engine: SqlEngine): boolean {
  return engine === 'postgres' || engine === 'oracle';
}

function leafQuotingFromRaw(qualified: QualifiedName, engine: SqlEngine) {
  if (!qualified.quoted || !qualified.raw) return 'unquoted' as const;
  // `raw` may carry a `schema.` chain; the leaf is its last segment.
  const leaf = lastRawSegment(qualified.raw);
  return inferQuotingFromRaw(leaf, engine) ?? ENGINE_DEFAULT_QUOTING[engine];
}

function lastRawSegment(raw: string): string {
  const close = raw[raw.length - 1];
  const open = close === ']' ? '[' : close === '"' || close === '`' ? close : null;
  if (open) {
    const start = raw.lastIndexOf(open, raw.length - 2);
    return start >= 0 ? raw.slice(start) : raw;
  }
  const dot = raw.lastIndexOf('.');
  return dot >= 0 ? raw.slice(dot + 1) : raw;
}
